import React, { useState } from 'react'
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {useNavigate} from "react-router-dom";

import VisibilityOff from '@mui/icons-material/VisibilityOffSharp';
import { RemoveRedEye as RemoveRedEyeIcon } from '@vedmarket/ui-kit';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField'
import InputAdornment from "@mui/material/InputAdornment";

import {useUpdatePasswordMutation} from "../../../api/auth";
import Notification, {NOTIFICATION_TYPE} from "../../common/notification";
import {PASSWORD_PATTERN} from "../../../utils/validation";
import {ROUTES} from "../../../config/constants";

import './new-password.scss'

enum REMEMBER_PASSWORD_FORM {
  PASSWORD = 'password',
  PASSWORD2 = 'password2',
  TOKEN = 'token',
}

type RememberPasswordForm = {
  password: string
  password2: string
  token: string
}

const NewPassword = () => {

  const { t } = useTranslation()

  const navigate = useNavigate();

  const [ isVisiblePassword, setIsVisiblePassword ] = useState(false);
  const [ isVisiblePasswordConfirm, setIsVisiblePasswordConfirm ] = useState(false);

  const [ isLoading, setIsLoading ] = useState(false);
  const [ commonError, setCommonError ] = useState(false);
  const [ passwordHasChangedSuccess, setPasswordHasChangedSuccess ] = useState(false);

  const [ updatePassword ] = useUpdatePasswordMutation()

  const { register, getValues, handleSubmit, trigger, setError, clearErrors, formState: { errors, isValid } } = useForm<RememberPasswordForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })

  const validatePasswordConfirm = (value: string) => value === getValues(REMEMBER_PASSWORD_FORM.PASSWORD) ? true: 'Пароли не совпадают'

  const onSubmit = handleSubmit(async (values) => {
    setIsLoading(true);

    try {
      const response: any = await updatePassword({ token: values.token, password: values.password})

      if (response?.error) {
        if (response.error.data.password) {
          setError(REMEMBER_PASSWORD_FORM.PASSWORD, { type: "custom",  message: response.error.data.password[0] })
        }
        throw response?.error;
      } else {
        setPasswordHasChangedSuccess(true);
        clearErrors([ REMEMBER_PASSWORD_FORM.PASSWORD, REMEMBER_PASSWORD_FORM.PASSWORD2 ])
      }

    } catch (error) {
      setCommonError(true);
    } finally {
      setIsLoading(false);
    }
  })

  const handleGoToLogin = () => {
    navigate(`${ROUTES.LOGIN}`);
  }

  if (passwordHasChangedSuccess){
    return <>
      <Notification
        type={NOTIFICATION_TYPE.SUCCESS}
        title={t('auth__message__passwordHasBeenSuccessfullyUpdated')}
      />
      <Button
        variant='contained'
        fullWidth
        sx={{
          marginTop: 2,
        }}
        onClick={handleGoToLogin}
      >{t('actions__text__login')}</Button>
    </>
  }

  return (
    <>
      <div className='new-password-form'>
        <TextField
          {...register(REMEMBER_PASSWORD_FORM.TOKEN, {
            required: t('common__text__isRequired'),
          })}
          error={Boolean(errors[REMEMBER_PASSWORD_FORM.TOKEN])}
          variant='outlined'
          label={t('common__text__token')}
          helperText={errors[REMEMBER_PASSWORD_FORM.TOKEN]?.message}
          fullWidth
          className='new-password-form__token'
        />
        <TextField
          {...register(REMEMBER_PASSWORD_FORM.PASSWORD, {
            required: t('common__text__isRequired'),
            pattern: { value: PASSWORD_PATTERN, message: t('errors__message__incorrectPassword')}
          })}
          className='new-password-form__field'
          error={Boolean(errors[REMEMBER_PASSWORD_FORM.PASSWORD])}
          variant='outlined'
          label={t('common__text__password')}
          type={isVisiblePassword? 'text' : 'password'}
          helperText={errors[REMEMBER_PASSWORD_FORM.PASSWORD]?.message}
          InputProps={{
            endAdornment: <InputAdornment position='start' onClick={() => setIsVisiblePassword(!isVisiblePassword)} className='auth__input-adornment'>{isVisiblePassword ? <VisibilityOff/> : <RemoveRedEyeIcon/>}</InputAdornment>,
          }}
          onBlur={async (e) => {
            await register(REMEMBER_PASSWORD_FORM.PASSWORD).onBlur(e);
            if (getValues(REMEMBER_PASSWORD_FORM.PASSWORD2)) {
              await trigger([ REMEMBER_PASSWORD_FORM.PASSWORD2 ])
            }
          }}
        />
        <TextField
          {...register(REMEMBER_PASSWORD_FORM.PASSWORD2, {
            required: t('common__text__isRequired'),
            pattern: { value: PASSWORD_PATTERN, message: t('errors__message__incorrectPassword')},
            validate: value => validatePasswordConfirm(value)
          })}
          className='new-password-form__field'
          error={Boolean(errors[REMEMBER_PASSWORD_FORM.PASSWORD2])}
          variant='outlined'
          label={t('common__text__confirmPassword')}
          type={isVisiblePasswordConfirm? 'text' : 'password'}
          helperText={errors[REMEMBER_PASSWORD_FORM.PASSWORD2]?.message}
          InputProps={{
            endAdornment: <InputAdornment position='start' onClick={() => setIsVisiblePasswordConfirm(!isVisiblePasswordConfirm)} className='auth__input-adornment'>{isVisiblePasswordConfirm ? <VisibilityOff/> : <RemoveRedEyeIcon/>}</InputAdornment>,
          }}
        />
      </div>
      <Button
        variant='contained'
        fullWidth
        sx={{
          marginTop: 2,
        }}
        disabled={!isValid}
        onClick={onSubmit}
      >{isLoading ? t('common__message__loadingText') : t('actions__message__saveLink')}</Button>
      {commonError && <Notification type={NOTIFICATION_TYPE.ERROR} title={t('errors__message__commonError')} />}
    </>
  )
}

export default NewPassword
